import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { PaymentConfirmationPreview, partyAndRowsForBillPayment, partyAndRowsForInvoiceDeposit, partyAndRowsForExpensePayment, buildPaymentConfirmationBlob, paymentConfirmationFilename } from "./paymentConfirmation";
import DraggableModal from "./DraggableModal";
import BulkDeleteDialog from "./BulkDeleteDialog";
import SearchSelect from "./SearchSelect";
import EmailDialog from "./EmailDialog";
import FileDropZone from "./FileDropZone";
import SentStatusIcon from "./SentStatusIcon";
import { loadCompanyProfile } from "./CompanyProfile";
import { appendDisclosureByAccount, filterContactsByCategory } from "./emailHelpers";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "./seasonFilter";
import ExportButton from "./ExportButton";
import { num2, s as xs } from "./xlsxExport";
import { loadFromSupabase, saveToSupabase, saveManyToSupabase, deleteFromSupabase } from "../supabaseDB";
import { uploadFileToStorage, publicUrlFor } from "../storageImages";
import { safeFilename } from "../pdfRenderer";

/* Same physical Supabase Storage bucket every other module reuses
   (ExpenseBillRegistry/ShipmentRegistry/ShipperPortal/SweetLeafPortal
   all point EXPENSE_DOCS_BUCKET/SHIPMENT_DOCS_BUCKET/SL_DOCS_BUCKET at
   this same literal "shipment-docs" — one bucket, per-file local alias,
   per CLAUDE.md's "duplicate small constants" convention). */
var PAYMENT_DOCS_BUCKET = "shipment-docs";
/* Local read of SweetLeaf's settings for appendDisclosureByAccount —
   Payment Confirmation emails to shippers always send from a
   Ground-Fresh account, so this never actually takes the SL branch,
   but the helper needs the arg present regardless. Same pattern as
   SalesReports.js's readSlSettingsForDisclosure. */
function readSlSettingsForDisclosure() { return loadLS("gfi-sl-settings", {}); }
/* Shipper-payment gate for the "Send Payment Confirmation" action
   (fix-dashboard.md Fase 2 Parte B CAMBIO 4): outbound (GF pays out,
   or legacy rows with no `type` — treated as outbound per the rest of
   this file's own sort/aggregate logic), never a SweetLeaf-sourced
   row (those live in SLPaymentsRegistry.js), and has at least one
   bill allocation (Bills are the shipper side of the ledger —
   invoice/expense-bill allocations are client deposits / vendor
   payments, out of scope for this shipper-only feature). */
function isShipperPayment(p) {
  return (p.type === "outbound" || !p.type) && p.source !== "sl" && (p.allocations || []).some(function (a) { return a.billId; });
}
/* 2026-09-15 — Arturo: "también debo enviar payment confirmation email
   a proveedores" — the Send Payment Confirmation button was gated by
   isShipperPayment alone, which only recognizes Bill (fruit) payments
   via allocations[].billId; expense-bill (service vendor) payments
   carry allocations[].expenseBillId instead and never qualified, so
   the button never appeared for a vendor payment at all. */
function isExpensePayment(p) {
  return (p.type === "outbound" || !p.type) && p.source !== "sl" && (p.allocations || []).some(function (a) { return a.expenseBillId; });
}
/* Builds the persisted shape for payment.extra.paymentConfirmationSent
   from EmailDialog's enriched onSent(payload) — { to, from, subject,
   attachmentNames }. Same shape/pattern as BillRegistry.js's
   buildSentInfo (fix-dashboard.md Fase 3 FEATURE 3), duplicated per
   CLAUDE.md's per-file small-helper convention rather than shared. */
function buildSentInfo(payload) {
  return {
    sentAt: new Date().toISOString(),
    sentTo: (payload && payload.to) || [],
    sentFrom: (payload && payload.from) || "",
    subject: (payload && payload.subject) || "",
    attachmentNames: (payload && payload.attachmentNames) || [],
  };
}
function resolveSupplierForPayment(payment) {
  var suppliers = loadLS("gfi-suppliers", []);
  var firstAlloc = payment.allocations && payment.allocations[0];
  /* 2026-09-15 — Arturo: "también debo enviar payment confirmation
     email a proveedores" — sending it for an expense-bill payment
     silently had no recipient, because this only ever looked up
     allocations[0].billId against gfi-bills (the shipper/fruit-bill
     shape). Expense-bill allocations carry expenseBillId instead (see
     partyAndRowsForExpensePayment), and ExpenseBillRegistry.js stores
     the vendor as a NAME with no id carried on the allocation — so the
     match here has to be by name. First attempt only searched
     gfi-suppliers and still missed real vendors ("sigue sin aparecer
     el domicilio") because ExpenseBillRegistry.js's own vendor picker
     (allVendorOptions) draws from FOUR catalogs — suppliers, clients,
     warehouses, AND brokers (a customs broker is a very common
     "vendor" for a service payment, e.g. Godinez International, which
     lives in gfi-brokers, not gfi-suppliers). Search the same four, in
     the same order, so any vendor the picker could have offered
     resolves here too — same fix mirrored in paymentConfirmation.js's
     partyAndRowsForExpensePayment. */
  if (firstAlloc && firstAlloc.expenseBillId) {
    var vendorName = firstAlloc.vendor || "";
    if (!vendorName) {
      var ebills = loadLS("gfi-expense-bills", []);
      var eb = ebills.find(function (x) { return x.id === firstAlloc.expenseBillId; });
      vendorName = eb ? (eb.vendor || "") : "";
    }
    if (!vendorName) return null;
    var target = String(vendorName).toLowerCase().trim();
    var found = null;
    ["gfi-suppliers", "gfi-clients", "gfi-warehouses", "gfi-brokers", "gfi-quality-control", "gfi-certifiers"].some(function (key) {
      var match = loadLS(key, []).find(function (s) { return String(s.shortName || s.fullName || s.name || s.companyName || "").toLowerCase().trim() === target; });
      if (match) { found = match; return true; }
      return false;
    });
    return found;
  }
  var bills = loadLS(BILLS_KEY, []);
  var firstBillId = firstAlloc && firstAlloc.billId;
  var bill = bills.find(function (b) { return b.id === firstBillId; });
  return bill ? suppliers.find(function (s) { return String(s.id) === String(bill.supplierId); }) : null;
}

/* ======================== TRANSLATIONS ======================== */
var T = {
  en: {
    title: "Payments", subtitle: "Payment & deposit history",
    search: "Search by reference, party or PAY#...",
    showing: "Showing", of: "of", total: "records", noResults: "No payments yet",
    noResultsHint: "Make a payment from Bills, Invoices, or Expense Bills to start tracking here.",
    payNumber: "PAY#", date: "Date", party: "Party", method: "Method",
    reference: "Reference", count: "Items", amount: "Amount", type: "Type",
    concept: "Concept", conceptPayment: "Payment", conceptAdvance: "Advance",
    allShippers: "All parties", allMethods: "All methods",
    filterAll: "All", filterPayments: "Payments only", filterDeposits: "Deposits only",
    typePayment: "Payment", typeDeposit: "Deposit",
    methodWire: "Wire", methodCheck: "Check", methodAch: "ACH", methodTransfer: "Transfer", methodCash: "Cash", methodOther: "Other",
    paymentDate: "Payment date", paymentMethod: "Method",
    edit: "Edit", delete: "Delete", close: "Close", save: "Save", cancel: "Cancel",
    viewPdf: "View PDF",
    confirmDel: "Delete this payment? All allocations will be reversed on the bills/invoices.",
    confirmDelTitle: "Confirm Delete",
    yes: "Yes, delete", no: "Keep it",
    deleted: "Payment deleted and balances reverted",
    edited: "Payment updated and balances recalculated",
    allocations: "Allocations applied",
    editTitle: "Edit Payment",
    notes: "Notes",
    summaryTotalPayments: "Total Payments", summaryTotalDeposits: "Total Deposits", summaryNetBalance: "Net Balance",
    paymentsCol: "Payments", depositsCol: "Deposits",
    overApply: "Amount cannot exceed bill total",
  },
  es: {
    title: "Pagos", subtitle: "Historial de pagos y depósitos",
    search: "Buscar por referencia, parte o PAY#...",
    showing: "Mostrando", of: "de", total: "registros", noResults: "Sin pagos aún",
    noResultsHint: "Haz un pago desde Bills, Facturas o Facturas de Gastos para empezar a registrar aquí.",
    payNumber: "Pago#", date: "Fecha", party: "Parte", method: "Método",
    reference: "Referencia", count: "Items", amount: "Monto", type: "Tipo",
    concept: "Concepto", conceptPayment: "Pago", conceptAdvance: "Adelanto",
    allShippers: "Todos", allMethods: "Todos",
    filterAll: "Todos", filterPayments: "Sólo Pagos", filterDeposits: "Sólo Depósitos",
    typePayment: "Pago", typeDeposit: "Depósito",
    methodWire: "Transferencia", methodCheck: "Cheque", methodAch: "ACH", methodTransfer: "Transferencia", methodCash: "Efectivo", methodOther: "Otro",
    paymentDate: "Fecha del pago", paymentMethod: "Método",
    edit: "Editar", delete: "Eliminar", close: "Cerrar", save: "Guardar", cancel: "Cancelar",
    viewPdf: "Ver PDF",
    confirmDel: "¿Eliminar este pago? Todas las asignaciones se revertirán de los bills/facturas.",
    confirmDelTitle: "Confirmar Eliminación",
    yes: "Sí, eliminar", no: "Mantener",
    deleted: "Pago eliminado y saldos revertidos",
    edited: "Pago actualizado y saldos recalculados",
    allocations: "Asignaciones aplicadas",
    editTitle: "Editar Pago",
    notes: "Notas",
    summaryTotalPayments: "Total Pagos", summaryTotalDeposits: "Total Depósitos", summaryNetBalance: "Saldo Neto",
    paymentsCol: "Pagos", depositsCol: "Depósitos",
    overApply: "El monto no puede exceder el total del bill",
  },
};

/* ======================== CONSTANTS ======================== */
var STORAGE_KEY = "gfi-payments";
var BILLS_KEY = "gfi-bills";
var INVOICES_KEY = "gfi-invoices";
var EXPENSE_BILLS_KEY = "gfi-expense-bills";
var PAYMENT_METHODS = ["wire", "check", "ach", "transfer", "cash", "other"];
var TYPE_STYLES = {
  outbound: { bg: "rgba(220,38,38,0.10)", text: "#DC2626", border: "rgba(220,38,38,0.25)" },
  inbound:  { bg: "rgba(16,185,129,0.10)", text: "#059669", border: "rgba(16,185,129,0.25)" },
};
/* 2026-10-21, Arturo: "matea toda la app" — same rectangular/3D-bevel
   recipe as ShipmentRegistry.js's TEXAS_QC_BEVEL, applied to the
   Inbound/Outbound type badge in this file so it reads the same
   squared, beveled way as every other status pill in the app. */
var TYPE_STATUS_BEVEL = "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)";
var inp = { padding: "7px 8px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

/* ======================== HELPERS ======================== */
function loadLS(key, fb) { try { var d = localStorage.getItem(key); return d ? JSON.parse(d) : fb; } catch { return fb; } }
function saveLS(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch (e) { console.error("[Payments] storage error", key, e); return false; } }

function fmtDate(d) { if (!d) return "—"; var dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }); }
function fmtMoney(n) { var v = parseFloat(n) || 0; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function calcBillSubtotal(bill) {
  var grossSales = 0;
  (bill.lines || []).forEach(function (l) {
    if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
      grossSales += (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
    }
  });
  var totalFees = 0;
  (bill.fees || []).forEach(function (f) { totalFees += parseFloat(f.amount) || 0; });
  var commAmt = grossSales * ((parseFloat(bill.commissionRate) || 0) / 100);
  return grossSales - commAmt - totalFees;
}

function calcInvoiceTotal(inv) {
  var total = 0;
  (inv.lines || []).forEach(function (l) {
    if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
      total += (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
    }
  });
  var tFees = 0;
  (inv.fees || []).forEach(function (fe) { tFees += parseFloat(fe.amount) || 0; });
  return total - tFees;
}

/* Cross-store sync helpers. Source of truth = Supabase tables `bills`,
   `invoices`, `expense_bills`. Dual-write to localStorage so legacy sync
   consumers (ProfitTracker, Settlements, ImportFromExcel scans, etc.)
   keep working until they migrate. */
async function reverseAllStoresForPayment(payment) {
  var billIds = (payment.allocations || []).filter(function (a) { return a.billId; }).map(function (a) { return a.billId; });
  if (billIds.length) {
    var bills = await loadFromSupabase("bills", { force: true });
    var changed = [];
    var updated = bills.map(function (b) {
      if (billIds.indexOf(b.id) === -1) return b;
      var nextPays = (b.payments || []).filter(function (p) { return p.paymentId !== payment.id; });
      var paid = nextPays.reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var subtotal = calcBillSubtotal(b);
      var due = subtotal - paid;
      var newStatus = b.status;
      if (b.status === "paid" && due > 0.01) newStatus = "finalized";
      var next = { ...b, payments: nextPays, amountPaid: +paid.toFixed(2), status: newStatus };
      changed.push(next);
      return next;
    });
    if (changed.length) { await saveManyToSupabase("bills", changed); saveLS(BILLS_KEY, updated); }
  }
  var invIds = (payment.allocations || []).filter(function (a) { return a.invoiceId; }).map(function (a) { return a.invoiceId; });
  if (invIds.length) {
    var invoices = await loadFromSupabase("invoices", { force: true });
    var changedI = [];
    var updatedI = invoices.map(function (inv) {
      if (invIds.indexOf(inv.id) === -1) return inv;
      var nextPays = (inv.payments || []).filter(function (p) { return p.paymentId !== payment.id; });
      var paid = nextPays.reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var total = calcInvoiceTotal(inv);
      var due = total - paid;
      var newStatus = inv.status;
      if (inv.status === "paid" && due > 0.01) newStatus = "sent";
      var next = { ...inv, payments: nextPays, amountPaid: +paid.toFixed(2), status: newStatus };
      changedI.push(next);
      return next;
    });
    if (changedI.length) { await saveManyToSupabase("invoices", changedI); saveLS(INVOICES_KEY, updatedI); }
  }
  var ebIds = (payment.allocations || []).filter(function (a) { return a.expenseBillId; }).map(function (a) { return a.expenseBillId; });
  if (ebIds.length) {
    var ebills = await loadFromSupabase("expense_bills", { force: true });
    var changedE = [];
    var updatedE = ebills.map(function (b) {
      if (ebIds.indexOf(b.id) === -1) return b;
      var nextPays = (b.payments || []).filter(function (p) { return p.paymentId !== payment.id; });
      var next = { ...b, payments: nextPays };
      changedE.push(next);
      return next;
    });
    if (changedE.length) { await saveManyToSupabase("expense_bills", changedE); saveLS(EXPENSE_BILLS_KEY, updatedE); }
  }
}

/* Inserts (or replaces) the payment's allocations as fresh entries on the
   matching bills/invoices/expense bills. Used by the edit flow after a
   prior reverseAllStoresForPayment so the new amounts/dates always land
   in payments[] — even if the allocation set changed. */
async function applyPaymentToStores(payment) {
  var byBill = {}, byInv = {}, byExp = {};
  (payment.allocations || []).forEach(function (a) {
    if (a.billId) byBill[a.billId] = a;
    else if (a.invoiceId) byInv[a.invoiceId] = a;
    else if (a.expenseBillId) byExp[a.expenseBillId] = a;
  });

  if (Object.keys(byBill).length) {
    var bills = await loadFromSupabase("bills", { force: true });
    var changed = [];
    var updated = bills.map(function (b) {
      if (!(b.id in byBill)) return b;
      var entry = { paymentId: payment.id, date: payment.paymentDate, amount: +(parseFloat(byBill[b.id].amount) || 0), method: payment.paymentMethod, reference: payment.reference, concept: byBill[b.id].concept || "" };
      var nextPays = (b.payments || []).filter(function (p) { return p.paymentId !== payment.id; });
      nextPays.push(entry);
      var totalPaid = nextPays.reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var subtotal = calcBillSubtotal(b);
      var due = subtotal - totalPaid;
      var newStatus = b.status;
      if (b.status !== "draft" && due <= 0.01 && totalPaid > 0) newStatus = "paid";
      else if (b.status === "paid" && due > 0.01) newStatus = "finalized";
      var next = { ...b, payments: nextPays, amountPaid: +totalPaid.toFixed(2), status: newStatus };
      changed.push(next);
      return next;
    });
    if (changed.length) { await saveManyToSupabase("bills", changed); saveLS(BILLS_KEY, updated); }
  }
  if (Object.keys(byInv).length) {
    var invoices = await loadFromSupabase("invoices", { force: true });
    var changedI = [];
    var updatedI = invoices.map(function (inv) {
      if (!(inv.id in byInv)) return inv;
      var entry = { paymentId: payment.id, date: payment.paymentDate, amount: +(parseFloat(byInv[inv.id].amount) || 0), method: payment.paymentMethod, reference: payment.reference, concept: byInv[inv.id].concept || "" };
      var nextPays = (inv.payments || []).filter(function (p) { return p.paymentId !== payment.id; });
      nextPays.push(entry);
      var totalPaid = nextPays.reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var total = calcInvoiceTotal(inv);
      var due = total - totalPaid;
      var newStatus = inv.status;
      if (inv.status !== "draft" && due <= 0.01 && totalPaid > 0) newStatus = "paid";
      else if (inv.status === "paid" && due > 0.01) newStatus = "sent";
      var next = { ...inv, payments: nextPays, amountPaid: +totalPaid.toFixed(2), status: newStatus };
      changedI.push(next);
      return next;
    });
    if (changedI.length) { await saveManyToSupabase("invoices", changedI); saveLS(INVOICES_KEY, updatedI); }
  }
  if (Object.keys(byExp).length) {
    var ebills = await loadFromSupabase("expense_bills", { force: true });
    var changedE = [];
    var updatedE = ebills.map(function (b) {
      if (!(b.id in byExp)) return b;
      var entry = { paymentId: payment.id, date: payment.paymentDate, amount: +(parseFloat(byExp[b.id].amount) || 0), method: payment.paymentMethod, reference: payment.reference, concept: byExp[b.id].concept || "" };
      var nextPays = (b.payments || []).filter(function (p) { return p.paymentId !== payment.id; });
      nextPays.push(entry);
      var totalPaid = nextPays.reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var next = { ...b, payments: nextPays, amountPaid: +totalPaid.toFixed(2) };
      changedE.push(next);
      return next;
    });
    if (changedE.length) { await saveManyToSupabase("expense_bills", changedE); saveLS(EXPENSE_BILLS_KEY, updatedE); }
  }
}

/* ======================== TOAST ======================== */
function Toast({ message, onClose }) {
  var [fading, setFading] = useState(false);
  useEffect(function () {
    var fadeT = setTimeout(function () { setFading(true); }, 2700);
    var closeT = setTimeout(function () { onClose(); }, 3000);
    return function () { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]);
  return createPortal(<div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1a2636", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 999999, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", animation: fading ? "fadeOut 0.3s ease forwards" : undefined }}>{message}</div>, document.body);
}

/* ======================== EDIT MODAL ======================== */
function EditPaymentModal({ payment, t, lang, onSave, onCancel, embedded }) {
  var [form, setForm] = useState({
    paymentDate: payment.paymentDate || "",
    paymentMethod: payment.paymentMethod || "wire",
    reference: payment.reference || "",
    notes: payment.notes || "",
  });
  /* Allocation amount is stored as a RAW STRING (`amountStr`) while
     the user is typing, NOT parsed to a Number. The previous version
     ran `parseFloat` on every keystroke, which mangled input the
     instant the user typed a decimal point ("1500." → 1500 → input
     re-renders as "1500" with the dot gone, next "0" becomes "15000"
     instead of "1500.0"). The numeric `amount` is recomputed once on
     blur / save via parseFloat.

     Initial amountStr seeds from a.amount (the persisted number) but
     keeps "" for empty cells so we don't show "0" in a blank input. */
  var [allocs, setAllocs] = useState((payment.allocations || []).map(function (a) {
    return Object.assign({}, a, {
      amountStr: (a.amount === undefined || a.amount === null || a.amount === "")
        ? ""
        : String(a.amount),
    });
  }));
  /* Column sort state for the allocations table. Defaults to
     ascending by reference (lot/PU) per spec — when an admin pays
     multiple bills in one payment, the lines now sort numerically
     (58025, 58026, 58027) instead of insertion order. */
  var [sortCol, setSortCol] = useState("ref");
  var [sortDir, setSortDir] = useState("asc");
  var [error, setError] = useState("");

  var methodLabel = function (m) { return t["method" + m.charAt(0).toUpperCase() + m.slice(1)] || m; };
  var total = useMemo(function () {
    return allocs.reduce(function (s, a) { return s + (parseFloat(a.amountStr) || 0); }, 0);
  }, [allocs]);
  var keyOf = function (a) { return a.billId || a.invoiceId || a.expenseBillId; };

  /* onChange: store the raw string verbatim — no parseFloat, no
     conversion, no auto-formatting. Whatever the user typed is what
     stays in state. */
  var setAmtStr = function (idx, raw) {
    setAllocs(function (prev) { return prev.map(function (a, i) { return i === idx ? Object.assign({}, a, { amountStr: raw }) : a; }); });
    setError("");
  };
  var setAllocConcept = function (idx, val) {
    setAllocs(function (prev) { return prev.map(function (a, i) { return i === idx ? Object.assign({}, a, { concept: val }) : a; }); });
  };
  /* onBlur: normalize what the user typed to a 2-decimal display
     ("1500" → "1500.00", "1500.5" → "1500.50") so the display
     settles. parseFloat tolerates the user's whitespace / trailing
     dot. An empty string stays empty. */
  var normalizeAmtStr = function (idx) {
    setAllocs(function (prev) { return prev.map(function (a, i) {
      if (i !== idx) return a;
      var raw = a.amountStr;
      if (raw === undefined || raw === null || String(raw).trim() === "") return Object.assign({}, a, { amountStr: "" });
      var n = parseFloat(raw);
      if (!isFinite(n)) return a; /* leave invalid text alone so the user can see + correct it */
      return Object.assign({}, a, { amountStr: n.toFixed(2) });
    }); });
  };

  var handleSave = function () {
    if (!form.paymentDate) { setError(lang === "es" ? "Selecciona una fecha" : "Select a date"); return; }
    if (total <= 0) { setError(lang === "es" ? "Total debe ser > 0" : "Total must be > 0"); return; }
    /* Validate each amount > 0 — read the raw string + parseFloat
       here (single conversion point); reject if it's blank/zero. */
    for (var i = 0; i < allocs.length; i++) {
      var a = allocs[i];
      if (!((parseFloat(a.amountStr) || 0) > 0)) { setError(lang === "es" ? "Todos los montos deben ser > 0" : "All amounts must be > 0"); return; }
    }
    onSave({
      ...payment,
      paymentDate: form.paymentDate,
      paymentMethod: form.paymentMethod,
      reference: form.reference,
      notes: form.notes,
      /* Persist a clean numeric `amount` AND drop the transient
         `amountStr` so the saved row matches the legacy shape. */
      allocations: allocs.map(function (a) {
        var n = +(parseFloat(a.amountStr) || 0).toFixed(2);
        var clean = Object.assign({}, a, { amount: n });
        delete clean.amountStr;
        return clean;
      }),
    });
  };

  var body = (
    <>
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.paymentDate}</label>
              <input type="date" value={form.paymentDate} onChange={function (e) { setForm({ ...form, paymentDate: e.target.value }); }} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.method}</label>
              <SearchSelect value={form.paymentMethod} onChange={v => setForm({ ...form, paymentMethod: v })} options={PAYMENT_METHODS.map(function (m) { return { value: m, label: methodLabel(m) }; })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.reference}</label>
              <input type="text" value={form.reference} onChange={function (e) { setForm({ ...form, reference: e.target.value }); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
          </div>

          {(function () {
            /* Sortable allocations table. Default sort: lot/PU/id
               ascending — addresses the "líneas en orden random"
               report. Every header is clickable; first click sorts
               asc, second toggles desc. The ORIGINAL alloc index is
               preserved on a private `_idx` so input onChange handlers
               still write back to the correct row even when the
               displayed order is different. */
            var sortKeyForRef = function (a) {
              /* Numeric-first sort key: prefer lot (numeric), then
                 puNumber, then expense reference string, then the
                 raw id as a last resort. Numeric values sort ahead
                 of strings so "58027" sits before "EB-001". */
              var n = parseInt(a.lot, 10);
              if (!isNaN(n)) return { kind: 0, n: n, s: "" };
              n = parseInt(a.puNumber, 10);
              if (!isNaN(n)) return { kind: 0, n: n, s: "" };
              return { kind: 1, n: 0, s: String(keyOf(a) || a.reference || "") };
            };
            var sortKeyForParty = function (a) { return String(a.shipperName || a.clientName || a.vendor || ""); };
            var sortKeyForAmount = function (a) { return parseFloat(a.amountStr) || 0; };
            var compareBy = function (col, dir) {
              return function (a, b) {
                var av, bv;
                if (col === "ref") { av = sortKeyForRef(a); bv = sortKeyForRef(b); }
                else if (col === "party") { av = sortKeyForParty(a); bv = sortKeyForParty(b); }
                else { av = sortKeyForAmount(a); bv = sortKeyForAmount(b); }
                var cmp;
                if (col === "ref") {
                  cmp = av.kind - bv.kind;
                  if (cmp === 0) cmp = av.kind === 0 ? (av.n - bv.n) : av.s.localeCompare(bv.s);
                } else if (col === "party") {
                  cmp = av.localeCompare(bv);
                } else {
                  cmp = av - bv;
                }
                return dir === "asc" ? cmp : -cmp;
              };
            };
            var sorted = allocs.map(function (a, i) { return { a: a, _idx: i }; });
            sorted.sort(function (x, y) { return compareBy(sortCol, sortDir)(x.a, y.a); });
            var arrow = function (col) {
              if (sortCol !== col) return " ⇅";
              return sortDir === "asc" ? " ▲" : " ▼";
            };
            var headerStyle = { padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" };
            var headerStyleR = Object.assign({}, headerStyle, { textAlign: "right" });
            var toggleSort = function (col) {
              if (sortCol === col) setSortDir(function (d) { return d === "asc" ? "desc" : "asc"; });
              else { setSortCol(col); setSortDir("asc"); }
            };
            return (
              <div style={{ borderRadius: 10, border: "1px solid var(--color-border)", overflow: "hidden", marginBottom: 16 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead><tr style={{ background: "var(--color-bg)", borderBottom: "2px solid var(--color-border)" }}>
                    <th style={headerStyle}  onClick={function () { toggleSort("ref"); }}    title={lang === "es" ? "Ordenar por referencia" : "Sort by reference"}>Reference{arrow("ref")}</th>
                    <th style={headerStyle}  onClick={function () { toggleSort("party"); }}  title={lang === "es" ? "Ordenar por contraparte" : "Sort by party"}>{t.party}{arrow("party")}</th>
                    <th style={headerStyleR} onClick={function () { toggleSort("amount"); }} title={lang === "es" ? "Ordenar por monto" : "Sort by amount"}>{t.amount}{arrow("amount")}</th>
                    <th style={Object.assign({}, headerStyle, { textAlign: "center", cursor: "default" })}>{t.concept}</th>
                  </tr></thead>
                  <tbody>{sorted.map(function (entry) {
                    var a = entry.a; var idx = entry._idx; /* WRITE BACK to original index */
                    return <tr key={idx} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "8px 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text)" }}>{keyOf(a)}{a.lot ? " · LOT " + a.lot : ""}</td>
                      <td style={{ padding: "8px 10px", fontSize: 12 }}>{a.shipperName || a.clientName || a.vendor || "—"}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>
                        {/* text input (NOT type=number) + raw-string
                            state + onBlur normalize. See setAmtStr /
                            normalizeAmtStr above for the rationale. */}
                        <input
                          type="text"
                          inputMode="decimal"
                          value={a.amountStr === undefined ? "" : a.amountStr}
                          onChange={function (e) { setAmtStr(idx, e.target.value); }}
                          onBlur={function () { normalizeAmtStr(idx); }}
                          style={{ ...inp, width: 130, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, marginLeft: "auto" }}
                        />
                      </td>
                      <td style={{ padding: "6px 10px", width: 130 }}>
                        <SearchSelect value={a.concept || ""} onChange={function (v) { setAllocConcept(idx, v); }} options={[{ value: "", label: t.conceptPayment }, { value: "advance", label: t.conceptAdvance }]} />
                      </td>
                    </tr>;
                  })}</tbody>
                  <tfoot><tr style={{ borderTop: "2px solid var(--color-accent)", background: "var(--color-bg)" }}>
                    <td colSpan={2} style={{ padding: "10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>Total</td>
                    <td style={{ padding: "10px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: 14, color: "var(--color-accent)" }}>{fmtMoney(total)}</td>
                    <td></td>
                  </tr></tfoot>
                </table>
              </div>
            );
          })()}

          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.notes}</label>
            <textarea value={form.notes} onChange={function (e) { setForm({ ...form, notes: e.target.value }); }} rows={2} style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />
          </div>

          {error && <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 6, color: "#DC2626", fontSize: 12, fontWeight: 600 }}>{error}</div>}
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{t.cancel}</button>
          <button onClick={handleSave} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{t.save}</button>
        </div>
    </>
  );
  if (embedded) return body;
  return (
    <DraggableModal title={t.editTitle + "  ·  " + payment.id} onClose={onCancel} initialWidth={760} initialHeight={640} zIndex={10002}>
      {body}
    </DraggableModal>
  );
}

/* ======================== DELETE CONFIRM ======================== */
function DeleteConfirm({ payment, t, onConfirm, onCancel }) {
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "var(--color-bg-card-solid)", borderRadius: 12, maxWidth: 460, padding: 24, boxShadow: "0 16px 48px rgba(0,0,0,0.25)" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 700, color: "#DC2626" }}>{t.confirmDelTitle}</h3>
        <p style={{ margin: "0 0 6px", fontSize: 13, color: "var(--color-text)" }}>{t.confirmDel}</p>
        <p style={{ margin: "0 0 18px", fontSize: 12, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{payment.id}</p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13 }}>{t.no}</button>
          <button onClick={onConfirm} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: "#DC2626", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{t.yes}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ======================== DETAIL PANEL ======================== */
/* Unified view+edit modal per CLAUDE.md "REGLA DE VENTANAS". View mode shows
   the allocation detail; clicking Edit toggles to edit mode inside the SAME
   DraggableModal (renders EditPaymentModal with embedded=true). */
function DetailPanel({ payment, t, lang, onSave, onDelete, onViewPdf, onSendConfirmation, onClose }) {
  var [mode, setMode] = useState("view");
  /* Same sortable-table contract as EditPaymentModal: default asc by
     reference (lot/PU/id) so the line order is deterministic and
     matches what the user expects when reviewing what they paid. */
  var [sortCol, setSortCol] = useState("ref");
  var [sortDir, setSortDir] = useState("asc");
  var allBills = useMemo(function () { return loadLS(BILLS_KEY, []); }, [payment.id]);
  var allInvoices = useMemo(function () { return loadLS(INVOICES_KEY, []); }, [payment.id]);
  var allExpense = useMemo(function () { return loadLS(EXPENSE_BILLS_KEY, []); }, [payment.id]);
  var total = (payment.allocations || []).reduce(function (s, a) { return s + (parseFloat(a.amount) || 0); }, 0);
  var type = payment.type || "outbound";
  var ts = TYPE_STYLES[type] || TYPE_STYLES.outbound;
  var typeLabel = type === "inbound" ? t.typeDeposit : t.typePayment;
  var methodKey = "method" + (payment.paymentMethod || "other").charAt(0).toUpperCase() + (payment.paymentMethod || "other").slice(1);

  var refOf = function (a) {
    if (a.billId) { var b = allBills.find(function (x) { return x.id === a.billId; }); return b ? (a.billId + (b.lot ? " · LOT " + b.lot : "")) : a.billId; }
    if (a.invoiceId) { var inv = allInvoices.find(function (x) { return x.id === a.invoiceId; }); return inv ? (a.invoiceId + (inv.puNumber ? " · PU " + inv.puNumber : "")) : a.invoiceId; }
    /* 2026-09-15 — Arturo: "la referencia de pago debe ser unicamente
       el numero de factura nada mas" (scoped to expense-bill payments
       only, per his own follow-up — shipper/Bill payments above are
       untouched). This was the visible "Asignaciones Aplicadas" table
       in the app itself — same EXP-#### + invoice concatenation bug
       already fixed in the PDF's buildExpenseRef, just a separate
       function here. */
    if (a.expenseBillId) { var eb = allExpense.find(function (x) { return x.id === a.expenseBillId; }); return eb ? (eb.reference || a.expenseBillId) : (a.reference || a.expenseBillId); }
    return "—";
  };

  var handleSaveInline = function(updated) { onSave(updated); setMode("view"); };

  return (
    <DraggableModal title={payment.id + (mode === "edit" ? " · " + t.editTitle : "")} onClose={onClose} initialWidth={Math.min(1200, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(900, window.innerHeight - 60)}>
      {mode === "edit" ? (
        <EditPaymentModal payment={payment} t={t} lang={lang} onSave={handleSaveInline} onCancel={function() { setMode("view"); }} embedded={true} />
      ) : (
        <>
        <div style={{ padding: "18px 24px 14px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: "var(--color-accent)" }}>{payment.id}</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{payment.shipperName || (payment.allocations || []).map(function (a) { return a.shipperName || a.clientName || a.vendor; }).filter(Boolean).join(", ") || "—"}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center" }}>
              <span style={{ padding: "3px 10px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: ts.bg, color: ts.text, border: "1px solid " + ts.border, boxShadow: TYPE_STATUS_BEVEL }}>{typeLabel}</span>
              <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{fmtDate(payment.paymentDate)}</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={function () { onViewPdf(payment); }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #1B3A4B", background: "#1B3A4B", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{t.viewPdf}</button>
            {(isShipperPayment(payment) || isExpensePayment(payment)) && (
              <button onClick={function () { onSendConfirmation(payment); }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #1B3A4B", background: "transparent", color: "#1B3A4B", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{lang === "es" ? "Enviar Payment Confirmation" : "Send Payment Confirmation"}</button>
            )}
            <button onClick={function () { setMode("edit"); }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.edit}</button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 18px", marginBottom: 18, fontSize: 12 }}>
            <div><span style={{ color: "var(--color-text-muted)", fontSize: 10 }}>{t.paymentDate}</span><div style={{ fontWeight: 600 }}>{fmtDate(payment.paymentDate)}</div></div>
            <div><span style={{ color: "var(--color-text-muted)", fontSize: 10 }}>{t.method}</span><div style={{ fontWeight: 600 }}>{t[methodKey] || "—"}</div></div>
            <div><span style={{ color: "var(--color-text-muted)", fontSize: 10 }}>{t.reference}</span><div style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{payment.reference || "—"}</div></div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.allocations}</div>
          {(function () {
            var sortKeyRef = function (a) {
              var n = parseInt(a.lot, 10);
              if (!isNaN(n)) return { kind: 0, n: n, s: "" };
              n = parseInt(a.puNumber, 10);
              if (!isNaN(n)) return { kind: 0, n: n, s: "" };
              return { kind: 1, n: 0, s: String(a.billId || a.invoiceId || a.expenseBillId || a.reference || "") };
            };
            var compare = function (a, b) {
              var cmp;
              if (sortCol === "ref") {
                var ka = sortKeyRef(a), kb = sortKeyRef(b);
                cmp = ka.kind - kb.kind;
                if (cmp === 0) cmp = ka.kind === 0 ? (ka.n - kb.n) : ka.s.localeCompare(kb.s);
              } else if (sortCol === "party") {
                cmp = String(a.shipperName || a.clientName || a.vendor || "").localeCompare(String(b.shipperName || b.clientName || b.vendor || ""));
              } else {
                cmp = (parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0);
              }
              return sortDir === "asc" ? cmp : -cmp;
            };
            var sorted = (payment.allocations || []).slice().sort(compare);
            var arrow = function (col) { return sortCol !== col ? " ⇅" : (sortDir === "asc" ? " ▲" : " ▼"); };
            var thStyle  = { padding: "6px 6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" };
            var thStyleR = Object.assign({}, thStyle, { textAlign: "right" });
            var toggle = function (col) {
              if (sortCol === col) setSortDir(function (d) { return d === "asc" ? "desc" : "asc"; });
              else { setSortCol(col); setSortDir("asc"); }
            };
            return (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
                <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                  <th style={thStyle}  onClick={function () { toggle("ref"); }}>Reference{arrow("ref")}</th>
                  <th style={thStyle}  onClick={function () { toggle("party"); }}>{t.party}{arrow("party")}</th>
                  <th style={thStyleR} onClick={function () { toggle("amount"); }}>{t.amount}{arrow("amount")}</th>
                </tr></thead>
                <tbody>{sorted.map(function (a, i) {
                  return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "7px 6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{refOf(a)}</td>
                    <td style={{ padding: "7px 6px" }}>{a.shipperName || a.clientName || a.vendor || "—"}</td>
                    <td style={{ padding: "7px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{fmtMoney(a.amount)}</td>
                  </tr>;
                })}</tbody>
                <tfoot><tr style={{ borderTop: "2px solid var(--color-accent)" }}>
                  <td colSpan={2} style={{ padding: "8px 6px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>Total</td>
                  <td style={{ padding: "8px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: 14, color: "var(--color-accent)" }}>{fmtMoney(total)}</td>
                </tr></tfoot>
              </table>
            );
          })()}

          {payment.notes && <div style={{ marginTop: 14, padding: 12, background: "var(--color-bg)", borderRadius: 8, fontSize: 12, color: "var(--color-text-secondary)", whiteSpace: "pre-wrap" }}>{payment.notes}</div>}
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={function () { onDelete(payment); }} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(231,76,60,0.25)", background: "rgba(231,76,60,0.08)", color: "#DC2626", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{t.delete}</button>
        </div>
        </>
      )}
    </DraggableModal>
  );
}

/* Email Payment Confirmation to a shipper, with a mandatory bank-proof
   attachment (fix-dashboard.md Fase 2 Parte B) — thin wrapper around
   the shared <EmailDialog>, mirrors SalesReports.js's
   EmailSalesReportModal (same buildAttachments/postProcessHtmlBody
   pattern). Scoped to shipper (Bill-allocation) payments only — see
   isShipperPayment above, which gates the trigger button in
   DetailPanel. */
function EmailPaymentConfirmationModal({ payment, supplier, lang, onClose, onDocumentsSaved, onSent }) {
  /* 2026-10-30 — Arturo: "debo pioder anexar mas de un archivo, solo
     se deja subir uno" — now a list, not a single doc. Multiple bank
     proofs (e.g. 2 wire receipts for one payment) can be attached. */
  var [bankProofDocs, setBankProofDocs] = useState(function () {
    return (payment.documents || []).filter(function (d) { return d.kind === "bank_proof"; });
  });
  /* Raw Files kept in memory when uploaded THIS session, keyed by
     storagePath — used directly as the email attachment so we don't
     need to re-fetch them back from Storage. A doc uploaded in a prior
     session has no entry here and buildAttachments falls back to
     fetching its storagePath's public URL instead. */
  var bankProofFilesRef = useRef({});
  var [uploading, setUploading] = useState(false);
  var [uploadError, setUploadError] = useState(null);

  var company = useMemo(function () { return loadCompanyProfile(); }, []);
  /* 2026-09-15 — computed once, reused below for both the party/rows
     shape and the bank-proof requirement (Arturo: "estos pagos [expense
     bills] no requieren el comprobante del banco obligatorio... es
     opcional" — only expense-bill payments relax that; Bill/shipper
     payments stay mandatory as originally spec'd). */
  var isExpense = (payment.allocations || []).some(function (a) { return a.expenseBillId; });
  /* 2026-09-15 — Arturo: this modal used to always build the Bill
     (fruit/shipper) party+rows shape regardless of the actual payment
     — harmless when the button was hidden for expense payments, but
     wrong now that it's also reachable for those (isExpensePayment,
     see DetailPanel above). partyAndRowsForBillPayment reads
     allocations[].billId, which an expense-bill payment doesn't carry,
     so it would have built an empty/wrong confirmation. */
  var partyRows = useMemo(function () {
    return isExpense ? partyAndRowsForExpensePayment(payment) : partyAndRowsForBillPayment(payment);
  }, [payment, isExpense]);
  /* 2026-09-15 — Arturo: "no agregaste al email de expense bills la
     firma" — emailSignatures is one flat, unbranded list (no GF/SL
     split the way email accounts have), so the global `isDefault` flag
     could point at anything or nothing; without an explicit pick
     EmailDialog only falls back to isDefault, which this Company
     Profile may not have set at all. Same "look up a signature named
     Ground first" pattern already used for the Protection Notice email
     (InvoiceRegistry.js) — this modal is Ground-Fresh-only (payment
     confirmations to shippers/vendors never go out as SweetLeaf). */
  var gfSignature = (company.emailSignatures || []).find(function (s) { return s && (s.name || "").toLowerCase().indexOf("ground") >= 0; })
    || (company.emailSignatures || []).find(function (s) { return s && s.isDefault; });
  var total = (payment.allocations || []).reduce(function (s, a) { return s + (parseFloat(a.amount) || 0); }, 0);
  var shortName = (supplier && (supplier.shortName || supplier.fullName || supplier.name)) || partyRows.party.name || "Shipper";
  /* Literal "Season" segment per fix-dashboard.md's exact template —
     "Payment_Confirmation_Season_{shipperShortName}_{paymentDate}_{totalAmountPaid}". */
  var subject = "Payment_Confirmation_Season_" + safeFilename(shortName) + "_" + (payment.paymentDate || "") + "_$" + total.toFixed(2);
  var pdfFilename = paymentConfirmationFilename("payment", shortName, payment.paymentDate);

  var recipients = useMemo(function () {
    var contacts = filterContactsByCategory(supplier && supplier.contacts, "payment_confirmation");
    var list = contacts.length > 0
      ? contacts.map(function (c) { return { name: c.name || "", email: c.email }; })
      : (supplier && supplier.email ? [{ name: supplier.shortName || supplier.fullName || "", email: supplier.email }] : []);
    return list.map(function (r) { return { name: r.name, email: r.email, checked: true }; });
  }, [supplier]);

  var defaultBody = lang === "es"
    ? "Saludos,\n\nLe informamos que el pago de las facturas referenciadas fue procesado hoy por Ground-Fresh International Inc."
    : "Greetings,\n\nPlease note the payment for the invoice(s) referenced was processed today by Ground-Fresh International Inc.";

  var ALLOWED_EXT = ["pdf", "jpg", "jpeg", "png"];
  var handleFiles = async function (files) {
    var list = Array.from(files || []);
    if (list.length === 0) return;
    var badFile = list.find(function (file) {
      var ext = (file.name.split(".").pop() || "").toLowerCase();
      return ALLOWED_EXT.indexOf(ext) === -1;
    });
    if (badFile) {
      setUploadError(lang === "es" ? "Solo se aceptan PDF, JPG o PNG" : "Only PDF, JPG, or PNG accepted");
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      var newDocs = [];
      for (var i = 0; i < list.length; i++) {
        var file = list[i];
        var ext = (file.name.split(".").pop() || "").toLowerCase();
        var path = "payments/" + payment.id + "/bank-proof-" + Date.now() + "-" + i + "." + ext;
        var up = await uploadFileToStorage(PAYMENT_DOCS_BUCKET, path, file);
        var doc = { name: file.name, size: file.size, type: file.type, storagePath: up.path, kind: "bank_proof" };
        bankProofFilesRef.current[doc.storagePath] = file;
        newDocs.push(doc);
      }
      var updatedDocs = bankProofDocs.concat(newDocs);
      var updated = { ...payment, documents: (payment.documents || []).filter(function (d) { return d.kind !== "bank_proof"; }).concat(updatedDocs) };
      await saveToSupabase("payments", updated);
      setBankProofDocs(updatedDocs);
      if (onDocumentsSaved) onDocumentsSaved(updated);
    } catch (e) {
      setUploadError(e && e.message ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };

  var removeBankProof = async function (doc) {
    var updatedDocs = bankProofDocs.filter(function (d) { return d.storagePath !== doc.storagePath; });
    var updated = { ...payment, documents: (payment.documents || []).filter(function (d) { return d.kind !== "bank_proof"; }).concat(updatedDocs) };
    try {
      await saveToSupabase("payments", updated);
      delete bankProofFilesRef.current[doc.storagePath];
      setBankProofDocs(updatedDocs);
      if (onDocumentsSaved) onDocumentsSaved(updated);
    } catch (e) {
      setUploadError(e && e.message ? e.message : String(e));
    }
  };

  var buildAttachments = async function () {
    var pdfBlob = await buildPaymentConfirmationBlob(payment, partyRows.party, partyRows.rows, lang);
    var out = [{ filename: pdfFilename, blob: pdfBlob, mimeType: "application/pdf" }];
    for (var i = 0; i < bankProofDocs.length; i++) {
      var doc = bankProofDocs[i];
      var proofBlob = bankProofFilesRef.current[doc.storagePath];
      if (!proofBlob && doc.storagePath) {
        var url = publicUrlFor(PAYMENT_DOCS_BUCKET, doc.storagePath);
        if (url) { var r = await fetch(url); proofBlob = await r.blob(); }
      }
      if (proofBlob) out.push({ filename: doc.name || ("BankProof_" + safeFilename(shortName) + "_" + (payment.paymentDate || "") + "_" + (i + 1)), blob: proofBlob, mimeType: doc.type || (proofBlob.type || "application/octet-stream") });
    }
    return out;
  };

  var sendDisabledReason = (!isExpense && bankProofDocs.length === 0)
    ? (lang === "es" ? "Sube el comprobante bancario antes de enviar" : "Upload the bank proof before sending")
    : "";

  /* 2026-09-15 — Arturo: "remueve la [opcion de] enviar del masked
     email de sweetleaf" — Payment Confirmation to a shipper/vendor
     must never go out as SweetLeaf (REGLA DE ENVÍO DE EMAILS: SL is
     Four Seasons/AGROCAP-only, from the SweetLeaf module). EmailDialog
     only shows the account picker when handed more than one, so
     filtering it out here removes the option entirely rather than
     just defaulting away from it. Same pattern already used for the
     Protection Notice email (InvoiceRegistry.js's gfOnlyAccounts). */
  /* 2026-09-30 — also excludes "automat" now (GFI Automated / SL
     Automated), same fix as InvoiceRegistry.js's gfOnlyAccounts; this
     filter only ever excluded "sweetleaf" in the name, which never
     matched those. */
  var gfOnlyAccounts = (company.emailAccounts || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });

  return (
    <EmailDialog
      lang={lang}
      title={(lang === "es" ? "Enviar Payment Confirmation" : "Send Payment Confirmation") + " — " + payment.id}
      recipients={recipients}
      docOptions={[]}
      defaultSubject={subject}
      defaultBody={defaultBody}
      fromEmail={(gfOnlyAccounts.find(function (a) { return a.maskedFrom; }) || {}).maskedFrom || company.outboundEmail || ""}
      emailAccounts={gfOnlyAccounts}
      signatures={company.emailSignatures || []}
      defaultSignatureId={gfSignature ? gfSignature.id : undefined}
      alwaysBuildAttachments={true}
      buildAttachments={buildAttachments}
      postProcessHtmlBody={function (html, resolvedFrom) { return appendDisclosureByAccount(html, resolvedFrom, company, readSlSettingsForDisclosure()); }}
      sendDisabledReason={sendDisabledReason}
      onSent={onSent}
      topContent={
        <div style={{ marginBottom: 12, padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{isExpense ? (lang === "es" ? "Comprobante bancario (opcional)" : "Bank Proof (optional)") : (lang === "es" ? "Comprobante bancario (obligatorio)" : "Bank Proof (required)")}</div>
          <FileDropZone lang={lang} multiple={true} disabled={uploading} onFiles={handleFiles}>
            <label style={{ fontSize: 12, color: "var(--color-accent)", cursor: "pointer", fontWeight: 600 }}>
              {uploading ? (lang === "es" ? "Subiendo…" : "Uploading…") : (bankProofDocs.length > 0 ? (lang === "es" ? "Agregar otro archivo" : "Add another file") : (lang === "es" ? "Elegir archivo(s)" : "Choose file(s)"))}
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: "none" }} disabled={uploading} onChange={function (e) { var files = e.target.files; e.target.value = ""; handleFiles(files); }} />
            </label>
          </FileDropZone>
          {bankProofDocs.map(function (doc) {
            return (
              <div key={doc.storagePath} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, color: "var(--color-text-secondary)", marginTop: 6 }}>
                <span>✓ {doc.name}</span>
                <button type="button" onClick={function () { removeBankProof(doc); }} disabled={uploading} style={{ border: "none", background: "none", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 700, padding: "0 4px" }}>✕</button>
              </div>
            );
          })}
          {uploadError && <div style={{ fontSize: 11, color: "#DC2626", marginTop: 6 }}>{uploadError}</div>}
        </div>
      }
      onClose={onClose}
    />
  );
}

/* ======================== MAIN REGISTRY ======================== */
export default function PaymentRegistry({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];
  var [payments, setPayments] = useState([]);
  var [loading, setLoading] = useState(true);
  var [viewing, setViewing] = useState(null);
  var [editing, setEditing] = useState(null);
  var [deleting, setDeleting] = useState(null);
  var [previewing, setPreviewing] = useState(null);
  var [emailConfirmationTarget, setEmailConfirmationTarget] = useState(null);
  var [toast, setToast] = useState(null);
  var [search, setSearch] = useState("");
  var [filterShipper, setFilterShipper] = useState("");
  var [filterMethod, setFilterMethod] = useState("");
  /* Season filter — multi-select grouped per CLAUDE.md "REGLA DE FILTRO
     DE SEASON". Pre-selects all Open seasons on mount. Season is derived
     from `payment.paymentDate` (the payment's own date). NOTE: a payment's
     allocations could in principle target bills/invoices belonging to a
     different season, but the user-facing question is "what was paid in
     which season?" — paymentDate is the right anchor. */
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [dateFrom, setDateFrom] = useState(""); var [dateTo, setDateTo] = useState("");
  /* Local season-name helper based on paymentDate. */
  var seasons = useMemo(function () { return loadLS("gfi-seasons", []); }, []);
  var seasonNameForPayment = useCallback(function (paymentDate) {
    if (!paymentDate) return "";
    var d = new Date(paymentDate + "T00:00:00");
    var s = seasons.find(function (s) { return new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"); });
    return s ? (s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4)) : "";
  }, [seasons]);
  var [typeFilter, setTypeFilter] = useState("all"); /* all | outbound | inbound */
  var [cardFilter, setCardFilter] = useState(null); /* null | thisMonth */
  var [sortCol, setSortCol] = useState(function() { try { var s = JSON.parse(localStorage.getItem("gfi-payments-sort") || "null"); return (s && s.column) || "paymentDate"; } catch { return "paymentDate"; } });
  var [sortDir, setSortDir] = useState(function() { try { var s = JSON.parse(localStorage.getItem("gfi-payments-sort") || "null"); return (s && s.direction) || "desc"; } catch { return "desc"; } });
  useEffect(function() { try { localStorage.setItem("gfi-payments-sort", JSON.stringify({ column: sortCol, direction: sortDir })); } catch {} }, [sortCol, sortDir]);
  var [selected, setSelected] = useState({});

  var [error, setError] = useState(null);
  var [saving, setSaving] = useState(false);
  var aliveRef = useRef(true);
  var paymentsRef = useRef([]);
  useEffect(function () { paymentsRef.current = payments; }, [payments]);

  /* Source of truth = Supabase `payments`. Dual-write to localStorage
     `gfi-payments` so legacy sync consumers (Settlements summaries,
     dashboards, etc.) keep working until they migrate. */
  var writePaymentsCache = function (next) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
  };
  /* PaymentRegistry is Ground Fresh only. SL deposits (SweetLeafPortal's
     handleBatchDepositConfirm) write into this SAME shared `payments`
     table with no way to tell them apart except this flag — that
     module tags every new record with source:"sl". Rows saved before
     the flag existed have no `source` at all and are treated as GF
     (backward compatibility; a one-time SQL backfill separately
     retags the historical SL ones). Filtering here — before the state
     even gets set — means the dashboard cards, exports and PDFs below
     all derive from GF-only data automatically, with no separate
     filter needed at each call site. Deliberately does NOT load
     sweetleaf_invoices or gfi-sl-invoices to make this determination —
     the source tag is the only signal this module is allowed to use. */
  var excludeSlPayments = function (rows) {
    return (rows || []).filter(function (p) { return p.source !== "sl"; });
  };
  var reload = async function () {
    setError(null); setLoading(true);
    try {
      var rows = excludeSlPayments(await loadFromSupabase("payments", { force: true }));
      if (aliveRef.current) { setPayments(rows); writePaymentsCache(rows); }
    } catch (e) {
      if (aliveRef.current) setError("Could not load payments: " + (e.message || String(e)));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };
  useEffect(function () {
    aliveRef.current = true;
    setError(null); setLoading(true);
    loadFromSupabase("payments")
      .then(function (rows) { if (aliveRef.current) { var gfRows = excludeSlPayments(rows); setPayments(gfRows); writePaymentsCache(gfRows); } })
      .catch(function (e) { if (aliveRef.current) setError("Could not load payments: " + (e.message || String(e))); })
      .finally(function () { if (aliveRef.current) setLoading(false); });
    return function () { aliveRef.current = false; };
  }, []);

  /* Open-by-id hook for global search */
  useEffect(function () {
    window.__gfiPaymentOpen = function (id) {
      var src = paymentsRef.current && paymentsRef.current.length ? paymentsRef.current : loadLS(STORAGE_KEY, []);
      var p = src.find(function (x) { return x.id === id; });
      if (p) setViewing(p);
    };
    return function () { delete window.__gfiPaymentOpen; };
  }, []);

  var handleDeleteConfirmed = async function () {
    if (!deleting || saving) return;
    var payment = (paymentsRef.current || []).find(function (p) { return p.id === deleting.id; });
    if (!payment) { setDeleting(null); return; }
    setSaving(true); setError(null);
    try {
      await reverseAllStoresForPayment(payment);
      await deleteFromSupabase("payments", payment.id);
      var nextPayments = (paymentsRef.current || []).filter(function (p) { return p.id !== deleting.id; });
      setPayments(nextPayments);
      writePaymentsCache(nextPayments);
      setDeleting(null);
      setViewing(null);
      setToast(t.deleted);
    } catch (e) {
      setError("Could not delete payment: " + (e.message || String(e)));
      setToast("⚠ " + (e.message || String(e)));
    } finally { setSaving(false); }
  };

  var handleEditSave = async function (updated) {
    /* Reverse the OLD payment's effects from every store, then apply the
       updated payment as fresh entries. This handles amount changes,
       date/method/reference changes, and even allocation set changes
       (records added or removed) without leaving stale entries behind. */
    if (saving) return;
    setSaving(true); setError(null);
    try {
      var oldPayment = (paymentsRef.current || []).find(function (p) { return p.id === updated.id; });
      if (oldPayment) await reverseAllStoresForPayment(oldPayment);
      await applyPaymentToStores(updated);
      await saveToSupabase("payments", updated);
      var next = (paymentsRef.current || []).map(function (p) { return p.id === updated.id ? updated : p; });
      setPayments(next);
      writePaymentsCache(next);
      setEditing(null);
      setViewing(updated);
      setToast(t.edited);
    } catch (e) {
      setError("Could not save payment: " + (e.message || String(e)));
      setToast("⚠ " + (e.message || String(e)));
    } finally { setSaving(false); }
  };

  /* Lightweight sync after EmailPaymentConfirmationModal uploads a bank
     proof — only `documents` changed (allocations/amounts didn't), so
     this skips handleEditSave's reverse/reapply-to-stores dance
     entirely and just patches the in-memory list + cache. The modal
     already called saveToSupabase itself before invoking this. */
  var handlePaymentDocumentsSaved = function (updated) {
    var next = (paymentsRef.current || []).map(function (p) { return p.id === updated.id ? updated : p; });
    setPayments(next);
    writePaymentsCache(next);
    if (viewing && viewing.id === updated.id) setViewing(updated);
  };

  var uniqueParties = useMemo(function () {
    var names = new Set();
    payments.forEach(function (p) {
      var allocs = p.allocations || [];
      /* p.shipperName is a comma-joined summary string on multi-party
         payments (see BillRegistry.js/InvoiceRegistry.js's payload
         builders), not a real party name — only use it here when there
         are no allocations to read the individual real names from. */
      if (allocs.length) {
        allocs.forEach(function (a) {
          if (a.shipperName) names.add(a.shipperName);
          if (a.clientName) names.add(a.clientName);
          if (a.vendor) names.add(a.vendor);
        });
      } else if (p.shipperName) {
        names.add(p.shipperName);
      }
    });
    return [...names].sort();
  }, [payments]);

  /* Season scope, isolated from the other row filters (type/search/
     shipper/method/date range). The summary cards below key off THIS
     array — not the fully-filtered `filtered` — because the cards
     themselves toggle typeFilter on click; if they read from a list
     that already had typeFilter baked in, clicking a card would
     collapse it against itself instead of letting the user compare
     Payments vs Deposits within the chosen season(s). Empty selection
     means "all seasons" (no restriction), same convention as
     Bills/Invoices/Settlements. */
  var seasonFilteredPayments = useMemo(function () {
    if (filterSeasons.length === 0) return payments;
    return payments.filter(function (p) { return filterSeasons.indexOf(resolveSeasonId(seasons, p.paymentDate, p.seasonId)) >= 0; });
  }, [payments, filterSeasons, seasons]);

  var filtered = useMemo(function () {
    var list = seasonFilteredPayments;
    if (typeFilter !== "all") list = list.filter(function (p) { return (p.type || "outbound") === typeFilter; });
    if (cardFilter === "thisMonth") {
      var nowD = new Date();
      var ymStr = nowD.getFullYear() + "-" + String(nowD.getMonth() + 1).padStart(2, "0");
      list = list.filter(function (p) { return (p.paymentDate || "").substring(0, 7) === ymStr; });
    }
    if (search) {
      var s = search.toLowerCase();
      list = list.filter(function (p) {
        return (p.id || "").toLowerCase().includes(s) ||
          (p.reference || "").toLowerCase().includes(s) ||
          (p.shipperName || "").toLowerCase().includes(s) ||
          (p.allocations || []).some(function (a) { return (a.shipperName || a.clientName || a.vendor || "").toLowerCase().includes(s); });
      });
    }
    if (filterShipper) list = list.filter(function (p) {
      return p.shipperName === filterShipper || (p.allocations || []).some(function (a) { return a.shipperName === filterShipper || a.clientName === filterShipper || a.vendor === filterShipper; });
    });
    if (filterMethod) list = list.filter(function (p) { return (p.paymentMethod || "") === filterMethod; });
    if (dateFrom || dateTo) list = list.filter(function (p) { var d = p.paymentDate || ""; if (!d) return false; if (dateFrom && d < dateFrom) return false; if (dateTo && d > dateTo) return false; return true; });
    return list;
  }, [seasonFilteredPayments, search, filterShipper, filterMethod, dateFrom, dateTo, typeFilter, cardFilter]);

  var sorted = useMemo(function () {
    return [...filtered].sort(function (a, b) {
      var va, vb;
      switch (sortCol) {
        case "id": va = a.id || ""; vb = b.id || ""; break;
        case "shipperName": va = a.shipperName || ""; vb = b.shipperName || ""; break;
        case "paymentDate": va = a.paymentDate || ""; vb = b.paymentDate || ""; break;
        case "amount":
          va = (a.allocations || []).reduce(function (s, x) { return s + (parseFloat(x.amount) || 0); }, 0);
          vb = (b.allocations || []).reduce(function (s, x) { return s + (parseFloat(x.amount) || 0); }, 0);
          return sortDir === "asc" ? va - vb : vb - va;
        case "paymentsAmt":
          va = (a.type === "outbound" || !a.type) ? (a.allocations || []).reduce(function (s, x) { return s + (parseFloat(x.amount) || 0); }, 0) : 0;
          vb = (b.type === "outbound" || !b.type) ? (b.allocations || []).reduce(function (s, x) { return s + (parseFloat(x.amount) || 0); }, 0) : 0;
          return sortDir === "asc" ? va - vb : vb - va;
        case "depositsAmt":
          va = a.type === "inbound" ? (a.allocations || []).reduce(function (s, x) { return s + (parseFloat(x.amount) || 0); }, 0) : 0;
          vb = b.type === "inbound" ? (b.allocations || []).reduce(function (s, x) { return s + (parseFloat(x.amount) || 0); }, 0) : 0;
          return sortDir === "asc" ? va - vb : vb - va;
        default: va = a[sortCol] || ""; vb = b[sortCol] || "";
      }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [filtered, sortCol, sortDir]);

  var toggleSort = function (col) { if (sortCol === col) setSortDir(function (d) { return d === "asc" ? "desc" : "asc"; }); else { setSortCol(col); setSortDir("asc"); } };
  var arrow = function (col) { return sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : ""; };

  var allSelected = sorted.length > 0 && sorted.every(function (p) { return selected[p.id]; });
  var toggleAll = function () { var ns = {}; if (!allSelected) sorted.forEach(function (p) { ns[p.id] = true; }); setSelected(ns); };
  var toggleOne = function (id, e) { if (e) e.stopPropagation(); setSelected(function (p) { var ns = Object.assign({}, p); if (ns[id]) delete ns[id]; else ns[id] = true; return ns; }); };
  var selectedCount = Object.keys(selected).length;
  var clearSelection = function () { setSelected({}); };
  var [showBulkDelete, setShowBulkDelete] = useState(false);

  /* Excel export. Summary = one row per payment (paymentDate, party,
     method, amount). Detail = one row per allocation — the natural
     grain when a single deposit pays multiple bills/invoices. */
  var paymentsToExport = function () {
    var sel = sorted.filter(function (p) { return selected[p.id]; });
    return sel.length > 0 ? sel : sorted;
  };
  var partyOf = function (p) {
    if (p.shipperName) return p.shipperName;
    if (p.clientName) return p.clientName;
    var fromAlloc = (p.allocations || []).map(function (a) { return a.shipperName || a.clientName || a.vendor; }).filter(Boolean);
    return fromAlloc.length > 0 ? fromAlloc.join(", ") : "";
  };
  var buildPmtSummaryRows = function () {
    return paymentsToExport().map(function (p) {
      var amt = (p.allocations || []).reduce(function (s, a) { return s + (parseFloat(a.amount) || 0); }, 0);
      return {
        ID: xs(p.id),
        Date: xs(p.paymentDate),
        Direction: xs(p.type),
        Party: xs(partyOf(p)),
        Method: xs(p.paymentMethod),
        Reference: xs(p.reference),
        Amount: num2(amt),
        Allocations: (p.allocations || []).length,
      };
    });
  };
  var buildPmtDetailRows = function () {
    var out = [];
    paymentsToExport().forEach(function (p) {
      var meta = { "Payment ID": xs(p.id), Date: xs(p.paymentDate), Direction: xs(p.type), Method: xs(p.paymentMethod), Reference: xs(p.reference) };
      (p.allocations || []).forEach(function (a) {
        var target = a.billId ? "Bill " + a.billId
          : a.invoiceId ? "Invoice " + a.invoiceId
          : a.expenseBillId ? "Expense " + a.expenseBillId
          : "—";
        out.push({
          ...meta,
          Type: "Allocation",
          Party: xs(a.shipperName || a.clientName || a.vendor),
          Target: target,
          Amount: num2(a.amount),
        });
      });
    });
    return out;
  };
  var handleBulkDelete = async function () {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      var current = paymentsRef.current || [];
      var toDelete = current.filter(function (p) { return selected[p.id]; });
      for (var i = 0; i < toDelete.length; i++) {
        await reverseAllStoresForPayment(toDelete[i]);
        await deleteFromSupabase("payments", toDelete[i].id);
      }
      var keep = current.filter(function (p) { return !selected[p.id]; });
      setPayments(keep); writePaymentsCache(keep);
      setShowBulkDelete(false); setSelected({}); setToast(t.deleted);
    } catch (e) {
      setError("Could not bulk delete payments: " + (e.message || String(e)));
      setToast("⚠ " + (e.message || String(e)));
    } finally { setSaving(false); }
  };

  /* Summary cards: Total Payments | Total Deposits | This Month.
     Keyed off seasonFilteredPayments (season scope only) instead of
     the raw `payments` array — these totals used to include the
     entire history regardless of which season(s) were selected. */
  var summary = useMemo(function () {
    var now = new Date();
    var ym = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");
    var r = { paymentsCount: 0, paymentsAmt: 0, depositsCount: 0, depositsAmt: 0, thisMonthCount: 0, thisMonthAmt: 0 };
    seasonFilteredPayments.forEach(function (p) {
      var amt = (p.allocations || []).reduce(function (s, a) { return s + (parseFloat(a.amount) || 0); }, 0);
      if (p.type === "inbound") { r.depositsCount++; r.depositsAmt += amt; }
      else { r.paymentsCount++; r.paymentsAmt += amt; }
      if ((p.paymentDate || "").substring(0, 7) === ym) { r.thisMonthCount++; r.thisMonthAmt += amt; }
    });
    return r;
  }, [seasonFilteredPayments]);

  /* Accounting-style breakdown for selection bar AND module-total
     chip. Reused by both via the same shape so the chips stay in
     lock-step. Module total uses `sorted` (the filtered view) so the
     chip reflects what the user is actually looking at. */
  function aggregatePayments(list) {
    var out = { payments: 0, deposits: 0 };
    (list || []).forEach(function (p) {
      var amt = (p.allocations || []).reduce(function (ss, a) { return ss + (parseFloat(a.amount) || 0); }, 0);
      if (p.type === "inbound") out.deposits += amt;
      else out.payments += amt;
    });
    out.net = out.deposits - out.payments;
    return out;
  }
  var selectedTotals = useMemo(function () { return aggregatePayments(payments.filter(function (p) { return selected[p.id]; })); }, [payments, selected]);
  var selectedNet = selectedTotals.net;
  var grandTotals = useMemo(function () { return aggregatePayments(sorted); }, [sorted]);
  var grandTotal = grandTotals.deposits + grandTotals.payments;

  /* Resolve party + rows for the PDF preview based on type */
  var openPdfFor = function (payment) {
    setPreviewing(payment);
  };
  var previewPartyRows = useMemo(function () {
    if (!previewing) return null;
    var p = previewing;
    var hasBill = (p.allocations || []).some(function (a) { return a.billId; });
    var hasInv = (p.allocations || []).some(function (a) { return a.invoiceId; });
    var hasEb = (p.allocations || []).some(function (a) { return a.expenseBillId; });
    if (hasBill) return partyAndRowsForBillPayment(p);
    if (hasInv) return partyAndRowsForInvoiceDeposit(p);
    if (hasEb) return partyAndRowsForExpensePayment(p);
    return { party: { name: p.shipperName || "—", lines: [] }, rows: [] };
  }, [previewing]);

  if (loading) return null;

  var th = { padding: "10px 8px", textAlign: "left", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", borderBottom: "2px solid var(--color-border)" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{t.title}</h2>
          <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{t.showing} {sorted.length} {t.of} {payments.length} {t.total} {"·"} {t.subtitle}</span>
        </div>
        <ExportButton lang={lang} filenameBase="Payments" selectedCount={selectedCount} totalCount={sorted.length} buildSummary={buildPmtSummaryRows} buildDetail={buildPmtDetailRows} />
      </div>
      {error && (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 6, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={function() { setError(null); reload(); }} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid #991B1B", background: "transparent", color: "#991B1B", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Reintentar" : "Retry"}</button>
        </div>
      )}

      {/* Summary cards: Total Payments (out) | Total Deposits (in) | Net Balance (in − out) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
        <div className={"summary-card" + (typeFilter === "outbound" && !cardFilter ? " active" : "")} onClick={function () { setCardFilter(null); setTypeFilter(typeFilter === "outbound" ? "all" : "outbound"); }}><div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600 }}>{t.summaryTotalPayments} ({summary.paymentsCount})</div><div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "#DC2626" }}>{fmtMoney(summary.paymentsAmt)}</div></div>
        <div className={"summary-card" + (typeFilter === "inbound" && !cardFilter ? " active" : "")} onClick={function () { setCardFilter(null); setTypeFilter(typeFilter === "inbound" ? "all" : "inbound"); }}><div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600 }}>{t.summaryTotalDeposits} ({summary.depositsCount})</div><div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "#059669" }}>{fmtMoney(summary.depositsAmt)}</div></div>
        <div className="summary-card"><div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600 }}>{t.summaryNetBalance}</div><div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: (summary.depositsAmt - summary.paymentsAmt) >= 0 ? "#059669" : "#DC2626" }}>{fmtMoney(summary.depositsAmt - summary.paymentsAmt)}</div></div>
      </div>

      {/* Type filter chips */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center" }}>
        {[{ k: "all", l: t.filterAll }, { k: "outbound", l: t.filterPayments }, { k: "inbound", l: t.filterDeposits }].map(function (f) {
          return <button key={f.k} onClick={function () { setTypeFilter(f.k); }} style={{
            padding: "6px 14px", borderRadius: 16, border: typeFilter === f.k ? "1px solid var(--color-accent)" : "1px solid var(--color-border)",
            background: typeFilter === f.k ? "var(--color-accent-bg)" : "transparent",
            color: typeFilter === f.k ? "var(--color-accent)" : "var(--color-text-secondary)",
            cursor: "pointer", fontSize: 12, fontWeight: typeFilter === f.k ? 700 : 500
          }}>{f.l}</button>;
        })}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder={t.search} style={{ ...inp, maxWidth: 280 }} />
        <SearchSelect value={filterShipper} onChange={setFilterShipper} placeholder={t.allShippers} style={{ minWidth: 170 }} options={uniqueParties.map(function (s) { return { value: s, label: s }; })} />
        <SearchSelect value={filterMethod} onChange={setFilterMethod} placeholder={t.allMethods} style={{ minWidth: 140 }} options={PAYMENT_METHODS.map(function (m) { return { value: m, label: t["method" + m.charAt(0).toUpperCase() + m.slice(1)] || m }; })} />
        <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} style={{ minWidth: 180 }} groups={buildSeasonFilterOptions(lang)} />
        <input type="date" value={dateFrom} onChange={function (e) { setDateFrom(e.target.value); }} title={lang === "es" ? "Desde" : "From"} style={{ ...inp, maxWidth: 140 }} />
        <input type="date" value={dateTo} onChange={function (e) { setDateTo(e.target.value); }} title={lang === "es" ? "Hasta" : "To"} style={{ ...inp, maxWidth: 140 }} />
        {(dateFrom || dateTo) && <button onClick={function () { setDateFrom(""); setDateTo(""); }} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>}
      </div>

      {selectedCount > 0 && <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(42,122,111,0.06)", border: "1px solid rgba(42,122,111,0.15)", marginBottom: 10, fontSize: 12 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={function () { setShowBulkDelete(true); }} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{"🗑 "}{lang === "es" ? "Eliminar" : "Delete Selected"}</button>
          <button onClick={clearSelection} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>
        </div>
        <div style={{ flex: 1, textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>
          <span style={{ fontWeight: 700, color: "var(--color-accent)" }}>{selectedCount} {lang === "es" ? "seleccionados" : "selected"}</span>
          <span style={{ color: "var(--color-text-muted)", margin: "0 8px" }}>|</span>
          <span>{t.depositsCol}: <strong style={{ fontFamily: "'JetBrains Mono', monospace", color: "#059669" }}>{fmtMoney(selectedTotals.deposits)}</strong></span>
          <span style={{ color: "var(--color-text-muted)", margin: "0 6px" }}>−</span>
          <span>{t.paymentsCol}: <strong style={{ fontFamily: "'JetBrains Mono', monospace", color: "#DC2626" }}>{fmtMoney(selectedTotals.payments)}</strong></span>
          <span style={{ color: "var(--color-text-muted)", margin: "0 6px" }}>=</span>
          <span>{lang === "es" ? "Neto" : "Net"}: <strong style={{ fontFamily: "'JetBrains Mono', monospace", color: selectedNet >= 0 ? "#059669" : "#DC2626" }}>{fmtMoney(selectedNet)} USD</strong></span>
        </div>
        <div style={{ fontSize: 10, color: "var(--color-text-muted)", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, minWidth: 200 }}>
          <div style={{ fontWeight: 700, color: "var(--color-text-secondary)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em" }}>{lang === "es" ? "Total módulo" : "Module total"}</div>
          <div style={{ display: "flex", gap: 8, fontSize: 10 }}>
            <span style={{ color: "#059669" }}>{fmtMoney(grandTotals.deposits)}</span>
            <span style={{ color: "#DC2626" }}>{fmtMoney(grandTotals.payments)}</span>
            <span style={{ color: grandTotals.net >= 0 ? "#059669" : "#DC2626", fontWeight: 700 }}>{fmtMoney(grandTotals.net)}</span>
          </div>
        </div>
      </div>}

      {sorted.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--color-text-muted)", fontSize: 14, background: "var(--color-bg)", borderRadius: 10, border: "1px dashed var(--color-border)" }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{t.noResults}</div>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t.noResultsHint}</div>
        </div>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid var(--color-border)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={{ ...th, width: 30, cursor: "default" }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
              <th onClick={function () { toggleSort("id"); }} style={th}>{t.payNumber}{arrow("id")}</th>
              <th onClick={function () { toggleSort("paymentDate"); }} style={th}>{t.date}{arrow("paymentDate")}</th>
              {/* 2026-09-15 — Arturo: "agregar la fecha a la tabla donde
                  se visualizan" — Processing/Delivery Date, captured on
                  the Batch Payment form for both Bill and Expense Bill
                  payments. "—" on older records / deposits, which never
                  set these fields. */}
              <th onClick={function () { toggleSort("processingDate"); }} style={th}>{t.processingDate}{arrow("processingDate")}</th>
              <th onClick={function () { toggleSort("deliveryDate"); }} style={th}>{t.deliveryDate}{arrow("deliveryDate")}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.type}</th>
              <th onClick={function () { toggleSort("shipperName"); }} style={th}>{t.party}{arrow("shipperName")}</th>
              <th style={th}>{t.method}</th>
              <th style={th}>{t.reference}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.count}</th>
              <th onClick={function () { toggleSort("paymentsAmt"); }} style={{ ...th, textAlign: "right" }}>{t.paymentsCol}{arrow("paymentsAmt")}</th>
              <th onClick={function () { toggleSort("depositsAmt"); }} style={{ ...th, textAlign: "right" }}>{t.depositsCol}{arrow("depositsAmt")}</th>
              <th style={{ ...th, textAlign: "center", cursor: "default" }}>{lang === "es" ? "Confirmación Enviada" : "Confirmation Sent"}</th>
            </tr></thead>
            <tbody>{sorted.map(function (p) {
              var amt = (p.allocations || []).reduce(function (s, a) { return s + (parseFloat(a.amount) || 0); }, 0);
              /* Type drives which money column the row's amount lands in:
                 outbound (bills / expense bills) = Payments (debit);
                 inbound (invoices) = Deposits (credit). The amount itself
                 is always positive; only its column changes. */
              var type = p.type || "outbound";
              var ts = TYPE_STYLES[type] || TYPE_STYLES.outbound;
              var typeLabel = type === "inbound" ? t.typeDeposit : t.typePayment;
              var methodKey = "method" + (p.paymentMethod || "other").charAt(0).toUpperCase() + (p.paymentMethod || "other").slice(1);
              var partyDisplay = p.shipperName || [...new Set((p.allocations || []).map(function (a) { return a.shipperName || a.clientName || a.vendor; }).filter(Boolean))].join(", ") || "—";
              /* 2026-10-16, Arturo: "solo ciertas partes del renglon son
                 clickeables y me cuesta trabajo abrirlos... el renglon
                 completo debe ser clickeable" — the <tr> itself already
                 carried the open-on-click handler, which normally makes
                 the WHOLE row clickable via event bubbling (empty
                 padding inside any <td> included) — but Arturo reports
                 real dead zones in some of that padding. Rather than
                 keep relying on bubbling through whatever's rendered
                 inside each cell, every <td> now carries the SAME
                 onClick directly (except the checkbox/status-icon cells,
                 which keep their own stopPropagation) — guarantees the
                 full cell box opens the record no matter what's inside. */
              var openRow = function () { setViewing(p); };
              return (
                <tr key={p.id} onClick={openRow} style={{ cursor: "pointer", transition: "background 0.15s", borderBottom: "1px solid var(--color-border)", background: selected[p.id] ? "rgba(42,122,111,0.04)" : "transparent" }} onMouseEnter={function (e) { e.currentTarget.style.background = selected[p.id] ? "rgba(42,122,111,0.08)" : "var(--color-bg-hover)"; }} onMouseLeave={function (e) { e.currentTarget.style.background = selected[p.id] ? "rgba(42,122,111,0.04)" : "transparent"; }}>
                  <td style={{ padding: "9px 8px", textAlign: "center" }} onClick={function (e) { e.stopPropagation(); }}><input type="checkbox" checked={!!selected[p.id]} onChange={function (e) { toggleOne(p.id, e); }} /></td>
                  <td onClick={openRow} style={{ padding: "9px 8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: "var(--color-accent)" }}>{p.id}</td>
                  <td onClick={openRow} style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(p.paymentDate)}</td>
                  <td onClick={openRow} style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{p.processingDate ? fmtDate(p.processingDate) : "—"}</td>
                  <td onClick={openRow} style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{p.deliveryDate ? fmtDate(p.deliveryDate) : "—"}</td>
                  <td onClick={openRow} style={{ padding: "9px 8px", textAlign: "center" }}><span style={{ padding: "2px 8px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: ts.bg, color: ts.text, border: "1px solid " + ts.border, boxShadow: TYPE_STATUS_BEVEL }}>{typeLabel}</span></td>
                  <td onClick={openRow} style={{ padding: "9px 8px", fontSize: 12, fontWeight: 600 }}>{partyDisplay}</td>
                  <td onClick={openRow} style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{t[methodKey] || "—"}</td>
                  <td onClick={openRow} style={{ padding: "9px 8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{p.reference || "—"}</td>
                  <td onClick={openRow} style={{ padding: "9px 8px", textAlign: "center", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{(p.allocations || []).length}</td>
                  <td onClick={openRow} style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: "#DC2626" }}>{type === "outbound" ? fmtMoney(amt) : "—"}</td>
                  <td onClick={openRow} style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: "#059669" }}>{type === "inbound" ? fmtMoney(amt) : "—"}</td>
                  {/* 2026-10-16, Arturo: "en la columna confirmation
                      sent no responde a clicks" — SentStatusIcon is a
                      plain display icon with just a `title` tooltip, no
                      click handler of its own, so the stopPropagation
                      here was blocking the row-open click for no actual
                      reason (nothing to protect it from). Opens the row
                      like every other cell now. */}
                  <td onClick={openRow} style={{ padding: "9px 8px", textAlign: "center" }}>
                    {(isShipperPayment(p) || isExpensePayment(p))
                      ? <SentStatusIcon sent={!!(p.extra && p.extra.paymentConfirmationSent)} details={p.extra && p.extra.paymentConfirmationSent} lang={lang} />
                      : <span style={{ color: "var(--color-text-muted)" }}>—</span>}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      {viewing && <DetailPanel
        payment={viewing}
        t={t} lang={lang}
        onSave={function(updated) { handleEditSave(updated); setViewing(updated); }}
        onDelete={function (p) { setDeleting(p); }}
        onViewPdf={openPdfFor}
        onSendConfirmation={function (p) {
          /* 2026-09-15 — Arturo: "no la veo" (la firma). loadCompanyProfile()
             only reads a synchronous in-memory cache — it never fetches
             itself. That cache is filled once by primeCatalogs() at app
             boot; if that hasn't landed yet in this browser tab (or
             failed), company.emailSignatures reads as [] and the
             defaultSignatureId "Ground" lookup below has nothing to
             find. Same fix already applied to the Protection Notice
             email (InvoiceRegistry.js's ensureCompanyProfileFresh) —
             refetch before opening the dialog so the signature is
             guaranteed to be there by render time. */
          loadFromSupabase("company_profile").catch(function () {}).then(function () { setEmailConfirmationTarget(p); });
        }}
        onClose={function () { setViewing(null); }}
      />}

      {editing && <EditPaymentModal
        payment={editing}
        t={t} lang={lang}
        onSave={handleEditSave}
        onCancel={function () { setEditing(null); }}
      />}

      {deleting && <DeleteConfirm
        payment={deleting}
        t={t}
        onConfirm={handleDeleteConfirmed}
        onCancel={function () { setDeleting(null); }}
      />}

      {showBulkDelete && <BulkDeleteDialog count={selectedCount} lang={lang} onCancel={function () { setShowBulkDelete(false); }} onConfirm={handleBulkDelete} />}

      {previewing && previewPartyRows && <PaymentConfirmationPreview
        payment={previewing}
        party={previewPartyRows.party}
        rows={previewPartyRows.rows}
        lang={lang}
        onClose={function () { setPreviewing(null); }}
      />}

      {emailConfirmationTarget && <EmailPaymentConfirmationModal
        payment={emailConfirmationTarget}
        supplier={resolveSupplierForPayment(emailConfirmationTarget)}
        lang={lang}
        onDocumentsSaved={handlePaymentDocumentsSaved}
        onSent={async function (payload) {
          var sentInfo = buildSentInfo(payload);
          /* Look up the LATEST record (not the closed-over prop) — a
             bank proof uploaded moments earlier in this same modal
             session already patched `documents` via
             onDocumentsSaved/handlePaymentDocumentsSaved, and stamping
             from a stale closure would silently regress that field. */
          var current = (paymentsRef.current || []).find(function (x) { return x.id === emailConfirmationTarget.id; }) || emailConfirmationTarget;
          var updated = { ...current, extra: { ...(current.extra || {}), paymentConfirmationSent: sentInfo } };
          try {
            await saveToSupabase("payments", updated);
            handlePaymentDocumentsSaved(updated);
          } catch (e) {
            console.warn("[PaymentRegistry] could not stamp paymentConfirmationSent:", e && e.message);
          }
        }}
        onClose={function () { setEmailConfirmationTarget(null); }}
      />}

      {toast && <Toast message={toast} onClose={function () { setToast(null); }} />}
    </div>
  );
}
